import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import useTranslate from '../../../../../hooks/useTranslate';
import useRadiologicalEquipmentForm from './useRadiologicalEquipmentForm';
import { useDosimetryForm } from './useDosimetryForm';
import { useResponsiblesForm } from './useResponsiblesForm';
import { RADIOLGICAL_EQUIPMENT_DRAWER_ACTION_VALUES } from '../components/drawers';
import {
    useDeleteCarcinogenicEquipmentMutation,
    useDeleteCarcinogenicEquipmentResponsibleMutation
} from '../../../../../services/carcinogenicApi';
import { enqueueSnackbar } from 'notistack';
import useAlerts from './useAlerts';
import { updateRadiologicalEquipmentEmptyDeclarationCheck } from '../../../../../redux/slices/carcinogenicReportSlice';
import { useDispatch } from 'react-redux';

const useRadiologicalEquipmentModule = ({ selectedRadiologicalEquipment, isEditing }: any) => {
    const dispatch = useDispatch();
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );
    const carcinogenicReport = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport
    );

    const radiologicalEquipmentEmptyDeclarationCheck = useAppSelector(
        (state) => state?.carcinogenicReport?.radiologicalEquipmentEmptyDeclarationCheck
    );

    const toggleRadiologicalEquipmentsEmptyDeclarationCheck = ({
        customValue
    }: {
        customValue?: boolean;
    }) => {
        dispatch(
            updateRadiologicalEquipmentEmptyDeclarationCheck(
                customValue !== undefined
                    ? customValue
                    : !radiologicalEquipmentEmptyDeclarationCheck
            )
        );
    };

    const [open, setOpen] = useState<string>('');
    const [selected, setSelected] = useState<any | null>(null);

    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.radiologicalEquipment'
    });
    const { translate } = useTranslate(t);

    const { showConfirmDeleteEquipmentAlert } = useAlerts({ translate });

    const handleEditIconClick = (item: any) => {
        setSelected(item);
        setOpen(RADIOLGICAL_EQUIPMENT_DRAWER_ACTION_VALUES.EDIT_RADIOLGICAL_EQUIPMENT);
    };

    const [deleteEquipment, { isLoading: isDeletingEquipment }] =
        useDeleteCarcinogenicEquipmentMutation();

    const handleDeleteEquipmentIconClick = async (selectedEquipment: any) => {
        showConfirmDeleteEquipmentAlert({
            confirmAction: () => handleDeleteEquipment(selectedEquipment)
        });
    };

    const handleDeleteEquipment = async (selectedEquipment: any) => {
        try {
            if (!selectedCarcinogenicReportId || !selectedEquipment?.id) return;
            const response: any = await deleteEquipment({
                reportId: selectedCarcinogenicReportId,
                substanceId: selectedEquipment?.sustancia?.id || 0,
                equipmentId: selectedEquipment.id
            });
            if (response?.error) {
                enqueueSnackbar(t('enqueues.deleteEquipment.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('enqueues.deleteEquipment.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            enqueueSnackbar(translate('enqueues.common.somethingWentWrong'), { variant: 'error' });
        }
    };

    const handleClickNewEquipmentButton = () => {
        setSelected(null);
        setOpen(RADIOLGICAL_EQUIPMENT_DRAWER_ACTION_VALUES.NEW_RADIOLGICAL_EQUIPMENT);
    };

    return {
        carcinogenicReport,
        selectedCarcinogenicReportId,
        selected,
        open,
        isDeletingEquipment,
        radiologicalEquipmentEmptyDeclarationCheck,
        toggleRadiologicalEquipmentsEmptyDeclarationCheck,
        handleDeleteEquipmentIconClick,
        handleClickNewEquipmentButton,
        handleEditIconClick,
        setSelected,
        setOpen,
        translate
    };
};

export default useRadiologicalEquipmentModule;
